import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Typography,
  Avatar,
  Tag,
  Spin,
  Modal,
  Select,
  Input,
  message as antdMessage,
  Space
} from 'antd';
import {
  SendOutlined,
  UserOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  UserAddOutlined,
  IdcardOutlined
} from '@ant-design/icons';
import { chatService, ChatMessage as ChatMessageType, TypingUser } from '../../services/chatService';
import api from '../../services/api';

interface ChatWindowProps {
  token: string;
  userId: string;
  userName: string;
  recipientId?: number; // ID du client avec qui on discute (pour l'admin)
  ticketStatus?: 'unassigned' | 'in_progress' | 'closed';
  assignedAgentName?: string | null;
  onMessageSent?: () => void; // Callback quand un message est envoyé
  onAssign?: () => void;
  onCloseTicket?: (category: string, notes: string) => void;
  onToggleCustomer360?: () => void;
  showCustomer360Button?: boolean;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ token, userId, userName, recipientId, ticketStatus, assignedAgentName, onMessageSent, onAssign, onCloseTicket, onToggleCustomer360, showCustomer360Button }) => {
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [typingUsers, setTypingUsers] = useState<Map<number, string>>(new Map());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const recipientIdRef = useRef<number | undefined>(recipientId); // Ref pour le recipientId actuel

  // Mettre à jour la ref quand recipientId change
  useEffect(() => {
    recipientIdRef.current = recipientId;
  }, [recipientId]);

  const loadChatHistory = async () => {
    try {
      // 💾 D'abord, charger le cache s'il existe
      const cachedMessages = chatService.loadCachedMessages(recipientId);
      if (cachedMessages.length > 0) {
        console.log(`📦 [ChatWindow] ${cachedMessages.length} messages restaurés depuis le cache`);
        setMessages(cachedMessages);
        scrollToBottom();
      }

      // Ensuite, charger l'historique depuis l'API
      const endpoint = recipientId 
        ? `/chat/conversation/${recipientId}?limit=100`
        : '/chat/history?limit=100';
        
      const response = await api.get(endpoint);

      if (response.data.success) {
        const apiMessages = response.data.data || response.data.messages || [];
        
        // 🔄 Fusionner intelligemment cache + API (éviter les doublons)
        const allMessages: ChatMessageType[] = [];
        const messageMap = new Map<number, ChatMessageType>();
        
        // Ajouter d'abord les messages du cache
        cachedMessages.forEach(msg => {
          if (msg.id > 0) {
            messageMap.set(msg.id, msg);
          } else {
            allMessages.push(msg);
          }
        });
        
        // Ajouter/écraser avec les messages de l'API
        apiMessages.forEach((msg: ChatMessageType) => {
          if (msg.id > 0) {
            messageMap.set(msg.id, msg);
          }
        });
        
        // Combiner et trier par date
        allMessages.push(...Array.from(messageMap.values()));
        allMessages.sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        
        console.log(`📜 [ChatWindow] Total après fusion: ${allMessages.length} messages (cache: ${cachedMessages.length}, API: ${apiMessages.length})`);
        
        setMessages(allMessages);
        
        // 💾 Mettre à jour le cache avec tous les messages fusionnés
        chatService.setCachedMessages(allMessages, recipientId);
        
        // Marquer les messages non lus comme lus
        const unreadMessageIds = allMessages
          .filter((msg: ChatMessageType) => !msg.is_read && msg.sender_role !== 'admin')
          .map((msg: ChatMessageType) => msg.id)
          .filter((id: number) => id > 0); // Exclure les IDs temporaires négatifs
        
        console.log('📖 [ChatWindow] Messages à marquer comme lus:', unreadMessageIds);
        
        if (unreadMessageIds.length > 0) {
          chatService.markAsRead(unreadMessageIds);
        }
        
        scrollToBottom();
      }
    } catch (error) {
      console.error('Erreur chargement historique chat:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Charger l'historique quand le composant monte OU quand recipientId change
    if (recipientId) {
      setIsLoading(true);
      setMessages([]); // Vider les anciens messages
      setTypingUsers(new Map()); // Vider les indicateurs de frappe
      loadChatHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientId]); // Recharger quand recipientId change

  useEffect(() => {
    // S'assurer que le socket est connecté
    if (!chatService.getConnectionStatus()) {
      chatService.connect(token, userId);
    }
    
    // Écouter les nouveaux messages
    const handleNewMessage = (message: ChatMessageType) => {
      // Utiliser la ref pour avoir toujours le recipientId actuel
      const currentRecipientId = recipientIdRef.current;
      
      // FILTRER : N'ajouter que les messages de la conversation active (sécurisé avec Number)
      const isFromCurrentClient = Number(message.sender_id) === Number(currentRecipientId);
      const isStaff = ['admin', 'super_admin', 'manager', 'agent'].includes(message.sender_role);
      const isToCurrentClient = isStaff && Number(message.recipient_id) === Number(currentRecipientId);
      
      if (!isFromCurrentClient && !isToCurrentClient) {
        return;
      }
      
      // Vérifier si le message n'existe pas déjà (éviter les doublons)
      setMessages((prev) => {
        // D'abord, chercher un message optimiste correspondant (ID négatif avec même contenu)
        const optimisticIndex = prev.findIndex((msg) => 
          msg.id < 0 && // Message optimiste (ID négatif)
          msg.message === message.message &&
          msg.sender_role === message.sender_role
        );
        
        // Si on trouve un message optimiste, le remplacer par le vrai message
        if (optimisticIndex !== -1) {
          console.log('🔄 [ChatWindow] Remplacement message optimiste par le vrai message ID:', message.id);
          const newMessages = [...prev];
          newMessages[optimisticIndex] = message;
          // 💾 Mettre à jour le cache
          chatService.setCachedMessages(newMessages, currentRecipientId);
          return newMessages;
        }
        
        // Sinon, vérifier les doublons normaux
        const exists = prev.some((msg) => {
          // Même ID positif = doublon certain
          if (msg.id === message.id && message.id > 0) return true;
          
          // Même contenu, même expéditeur, dans les 5 secondes = probable doublon
          const msgTime = new Date(msg.created_at).getTime();
          const newTime = new Date(message.created_at).getTime();
          const timeDiff = Math.abs(msgTime - newTime);
          
          return msg.message === message.message &&
                 String(msg.sender_id) === String(message.sender_id) && // Comparer comme strings
                 timeDiff < 5000; // 5 secondes au lieu de 2
        });
        
        if (exists) {
          console.log('⚠️ [ChatWindow] Message doublon ignoré:', message.id);
          return prev;
        }
        
        // 💾 Ajouter au cache
        chatService.addMessageToCache(message, currentRecipientId);
        
        return [...prev, message];
      });
      scrollToBottom();
    };

    chatService.onMessage(handleNewMessage);

    // Écouter les changements de connexion
    chatService.onConnectionChange((connected) => {
      setIsConnected(connected);
    });

    // Écouter les mises à jour de lecture des messages
    const handleMessagesRead = (data: { messageIds: number[] }) => {
      console.log('📖 [ChatWindow] Événement reçu:', data.messageIds.length, 'IDs =', data.messageIds);
      setMessages((prev) => {
        const updated = prev.map((msg) => {
          if (data.messageIds.includes(msg.id)) {
            console.log('✅ [ChatWindow] Message', msg.id, 'mis à jour isRead → true');
            return { ...msg, is_read: true };
          }
          return msg;
        });
        return updated;
      });
    };

    chatService.onMessagesRead(handleMessagesRead);

    // Écouter les confirmations d'envoi pour mettre à jour l'ID des messages optimistes
    const handleMessageSent = (data: { success: boolean; message: ChatMessageType }) => {
      if (!data.success || !data.message) return;
      
      const realMessage = data.message;
      const currentRecipientId = recipientIdRef.current;
      
      // Vérifier que c'est bien un message pour la conversation actuelle
      if (Number(realMessage.recipient_id) !== Number(currentRecipientId)) return;
      
      console.log('✅ [ChatWindow] Message envoyé confirmé, ID réel:', realMessage.id);
      
      setMessages((prev) => {
        // Chercher le message optimiste correspondant (ID négatif + même contenu)
        const optimisticIndex = prev.findIndex((msg) => 
          msg.id < 0 && // Message optimiste (ID négatif)
          msg.message === realMessage.message &&
          msg.sender_role === realMessage.sender_role
        );
        
        if (optimisticIndex !== -1) {
          console.log('🔄 [ChatWindow] Remplacement message optimiste par le vrai ID:', realMessage.id);
          const newMessages = [...prev];
          newMessages[optimisticIndex] = realMessage;
          // 💾 Mettre à jour le cache
          chatService.setCachedMessages(newMessages, currentRecipientId);
          return newMessages;
        }
        
        return prev;
      });
    };

    chatService.onMessageSent(handleMessageSent);

    // Écouter les indicateurs de frappe
    const handleTyping = (data: TypingUser) => {
      // Utiliser la ref pour avoir toujours le recipientId actuel
      const currentRecipientId = recipientIdRef.current;
      
      if (Number(data.userId) !== Number(currentRecipientId)) {
        return;
      }
      
      setTypingUsers((prev) => {
        const newMap = new Map(prev);
        newMap.set(data.userId, data.userName);
        return newMap;
      });
    };

    const handleStopTyping = (data: TypingUser) => {
      // Utiliser la ref pour avoir toujours le recipientId actuel
      const currentRecipientId = recipientIdRef.current;
      
      if (Number(data.userId) !== Number(currentRecipientId)) {
        return;
      }
      
      setTypingUsers((prev) => {
        const newMap = new Map(prev);
        newMap.delete(data.userId);
        return newMap;
      });
    };

    chatService.onTyping(handleTyping);
    chatService.onStopTyping(handleStopTyping);

    // Cleanup : supprimer les listeners au démontage du composant
    return () => {
      chatService.offMessage(handleNewMessage);
      chatService.offMessagesRead(handleMessagesRead);
      chatService.offMessageSent(handleMessageSent);
      chatService.offTyping(handleTyping);
      chatService.offStopTyping(handleStopTyping);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId]); // Ne pas inclure recipientId ici, on utilise la ref

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSendMessage = () => {
    if (inputMessage.trim() === '') return;

    const messageText = inputMessage.trim();
    
    // Affichage optimiste : ajouter le message immédiatement avec un ID négatif
    const optimisticMessage: ChatMessageType = {
      id: -Date.now(), // ID temporaire NÉGATIF pour identifier les messages optimistes
      sender_id: parseInt(userId),
      sender_role: 'admin',
      message: messageText,
      is_read: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      sender: {
        id: parseInt(userId),
        name: userName,
      },
    };

    setMessages((prev) => [...prev, optimisticMessage]);
    
    // 💾 Ajouter au cache
    chatService.addMessageToCache(optimisticMessage, recipientId);
    
    scrollToBottom();

    // Envoyer au serveur
    chatService.sendMessage(messageText, recipientId);
    setInputMessage('');
    chatService.stopTyping();

    // Si la conversation n'est pas encore prise en charge, la prendre en charge automatiquement à l'envoi du message
    if (ticketStatus === 'unassigned' && onAssign) {
      onAssign();
    } else if (onMessageSent) {
      onMessageSent();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);

    // Démarrer l'indicateur de frappe
    chatService.startTyping();

    // Arrêter après 2 secondes d'inactivité
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      chatService.stopTyping();
    }, 2000);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const renderMessage = (message: ChatMessageType) => {
    const isAdmin = ['admin', 'super_admin', 'manager', 'agent'].includes(message.sender_role);
    const senderName = message.sender?.name || message.sender?.username || 'Utilisateur';

    return (
      <div
        key={message.id}
        style={{
          display: 'flex',
          justifyContent: isAdmin ? 'flex-end' : 'flex-start',
          marginBottom: '16px',
        }}
      >
        {!isAdmin && (
          <Avatar
            style={{
              backgroundColor: '#2196f3',
              width: 32,
              height: 32,
              marginRight: 8,
            }}
          >
            {senderName.charAt(0).toUpperCase()}
          </Avatar>
        )}
        <div style={{ maxWidth: '70%' }}>
          {!isAdmin && (
            <Typography.Text type="secondary" style={{ marginLeft: 8, marginBottom: 4, display: 'block', fontSize: '0.75rem' }}>
              {senderName}
            </Typography.Text>
          )}
          <Card
            size="small"
            style={{
              padding: '12px',
              backgroundColor: isAdmin ? '#1976d2' : '#f5f5f5',
              color: isAdmin ? 'white' : 'inherit',
              borderRadius: isAdmin ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              border: 'none',
            }}
            bodyStyle={{ padding: 0 }}
          >
            <Typography.Text style={{ color: isAdmin ? 'white' : 'inherit' }}>{message.message}</Typography.Text>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 4 }}>
              <Typography.Text
                style={{ color: isAdmin ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.45)', fontSize: '0.7rem' }}
              >
                {formatTime(message.created_at)}
              </Typography.Text>
              {isAdmin && (
                <span style={{ 
                  marginLeft: 4, 
                  display: 'inline-flex', 
                  alignItems: 'center',
                  position: 'relative',
                  width: message.is_read ? 18 : 12,
                  height: 12,
                }}>
                  {/* Première coche */}
                  <CheckOutlined style={{ 
                    fontSize: 12, 
                    color: message.is_read ? '#4fc3f7' : 'rgba(255,255,255,0.7)',
                    position: 'absolute',
                    left: 0,
                  }} />
                  {/* Deuxième coche (seulement si lu) */}
                  {message.is_read && (
                    <CheckOutlined style={{ 
                      fontSize: 12, 
                      color: '#4fc3f7',
                      position: 'absolute',
                      left: 6,
                    }} />
                  )}
                </span>
              )}
            </div>
          </Card>
        </div>
      </div>
    );
  };

  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [ticketCategory, setTicketCategory] = useState('Information');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [submittingClose, setSubmittingClose] = useState(false);

  const handleConfirmClose = async () => {
    if (!recipientId || !onCloseTicket) return;
    const trimmed = resolutionNotes.trim();
    if (!trimmed) {
      antdMessage.error('Le compte-rendu de résolution est obligatoire pour clore le ticket support.');
      return;
    }
    setSubmittingClose(true);
    try {
      await onCloseTicket(ticketCategory, trimmed);
      setCloseModalOpen(false);
      setResolutionNotes('');
    } catch (e: any) {
      antdMessage.error('Erreur lors de la clôture');
    } finally {
      setSubmittingClose(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card
      style={{ 
        height: '100%', 
        width: '100%',
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      bodyStyle={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 0 }}
    >
      {/* En-tête fixe Call Center */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          backgroundColor: '#fff',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <UserOutlined style={{ color: '#0a543d', fontSize: 18 }} />
          <div>
            <Typography.Title level={5} style={{ margin: 0, fontSize: 15 }}>
              Discussion Support
            </Typography.Title>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              {ticketStatus === 'unassigned' ? (
                <Tag color="volcano" style={{ fontSize: 10, margin: 0, borderRadius: 6 }}>En attente de prise en charge</Tag>
              ) : ticketStatus === 'closed' ? (
                <Tag color="green" style={{ fontSize: 10, margin: 0, borderRadius: 6 }}>Ticket Résolu & Clos</Tag>
              ) : (
                <Tag color="blue" style={{ fontSize: 10, margin: 0, borderRadius: 6 }}>
                  Pris en charge par : {assignedAgentName || 'Agent'}
                </Tag>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {showCustomer360Button && onToggleCustomer360 && (
            <Button
              type="default"
              icon={<IdcardOutlined />}
              onClick={onToggleCustomer360}
              style={{ borderRadius: 8, borderColor: '#0a543d', color: '#0a543d', fontWeight: 600 }}
            >
              Fiche 360°
            </Button>
          )}

          {ticketStatus === 'unassigned' && onAssign && (
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              style={{ backgroundColor: '#16a34a', borderColor: '#16a34a', borderRadius: 8, fontWeight: 600 }}
              onClick={onAssign}
            >
              Prendre en charge
            </Button>
          )}

          {ticketStatus === 'in_progress' && onCloseTicket && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              style={{ backgroundColor: '#0a543d', borderColor: '#0a543d', borderRadius: 8, fontWeight: 600 }}
              onClick={() => {
                setResolutionNotes('');
                setCloseModalOpen(true);
              }}
            >
              Clore le Ticket
            </Button>
          )}

          <Button
            type="text"
            icon={<ReloadOutlined />}
            onClick={async () => {
              setIsLoading(true);
              await loadChatHistory();
              setIsLoading(false);
            }}
            title="Actualiser"
          />

          <Tag color={isConnected ? 'success' : 'error'}>
            <span style={{ fontSize: 8, marginRight: 4 }}>●</span>
            {isConnected ? 'En ligne' : 'Hors ligne'}
          </Tag>
        </div>
      </div>

      {/* Zone des messages scrollable */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: 16,
          backgroundColor: '#fafafa',
        }}
      >
        {messages.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'rgba(0, 0, 0, 0.45)',
            }}
          >
            <UserOutlined style={{ fontSize: 60, marginBottom: 16, opacity: 0.3 }} />
            <Typography.Text>Aucun message</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: '0.875rem' }}>Les messages clients apparaîtront ici</Typography.Text>
          </div>
        ) : (
          <>
            {messages.map((message) => renderMessage(message))}
            <div ref={messagesEndRef} />
          </>
        )}

        {/* Indicateur de frappe */}
        {typingUsers.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
            <Spin size="small" style={{ marginRight: 8 }} />
            <Typography.Text type="secondary" italic style={{ fontSize: '0.875rem' }}>
              {Array.from(typingUsers.values()).join(', ')} {typingUsers.size > 1 ? 'sont' : 'est'} en
              train d'écrire...
            </Typography.Text>
          </div>
        )}
      </div>

      {/* Zone de saisie fixe */}
      <div
        style={{
          padding: 16,
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'flex-end',
          gap: 8,
          flexShrink: 0,
          backgroundColor: '#fff',
        }}
      >
        <div style={{ flex: 1, minWidth: 0, maxWidth: 'calc(100% - 48px)' }}>
          <textarea
            placeholder="Tapez votre message..."
            value={inputMessage}
            onChange={(e) => handleInputChange(e as any)}
            onKeyDown={(e) => handleKeyDown(e as any)}
            disabled={!isConnected}
            rows={1}
            wrap="soft"
            style={{ 
              width: '100%',
              boxSizing: 'border-box',
              borderRadius: 16, 
              resize: 'none',
              padding: '8px 16px',
              border: '1px solid #d9d9d9',
              outline: 'none',
              fontFamily: 'inherit',
              fontSize: 14,
              lineHeight: '1.5',
              minHeight: 38,
              maxHeight: 120,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = Math.min(target.scrollHeight, 120) + 'px';
            }}
          />
        </div>
        <Button
          type="primary"
          shape="circle"
          icon={<SendOutlined />}
          onClick={handleSendMessage}
          disabled={!isConnected || inputMessage.trim() === ''}
        />
      </div>

      {/* MODAL CLÔTURE SUPPORT AVEC COMPTE-RENDU OBLIGATOIRE */}
      <Modal
        title={
          <Space>
            <CheckCircleOutlined style={{ color: '#0a543d' }} />
            <span>Clôture du Ticket Support Client</span>
          </Space>
        }
        open={closeModalOpen}
        onCancel={() => {
          if (!submittingClose) {
            setCloseModalOpen(false);
            setResolutionNotes('');
          }
        }}
        onOk={handleConfirmClose}
        confirmLoading={submittingClose}
        okText="Valider & Clore le Ticket"
        cancelText="Annuler"
        okButtonProps={{ style: { backgroundColor: '#0a543d', borderColor: '#0a543d', fontWeight: 600 } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
              Catégorie / Motif principal de l'échange :
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              value={ticketCategory}
              onChange={(val) => setTicketCategory(val)}
            >
              <Select.Option value="Information">Renseignement / Information Tarifaire</Select.Option>
              <Select.Option value="Depannage">Dépannage / Assistance Technique</Select.Option>
              <Select.Option value="Abonnement">Devis & Offre d'Abonnement</Select.Option>
              <Select.Option value="Reclamation">Réclamation Client</Select.Option>
              <Select.Option value="Autre">Autre</Select.Option>
            </Select>
          </div>

          <div>
            <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
              Compte-rendu de résolution (Obligatoire) :
            </Typography.Text>
            <Input.TextArea
              rows={4}
              placeholder="Ex: Le client a été renseigné sur les tarifs Confort. Il a souscrit l'offre en direct et un RDV de maintenance a été fixé au 18 août."
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default ChatWindow;
